import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Card, CardBody, Button, Row, Col, Badge } from "reactstrap";
import { Fn_AddEditData, Fn_FillListData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Save,
  ArrowLeft,
  Trash2,
  CheckCircle,
  RotateCcw,
  Maximize2,
  Edit2,
  Search,
  Check,
} from "react-feather";

const GOOGLE_MAPS_KEY = "AIzaSyBUv5ri7fIu-QtKOM-1LdVu0EozSigVQLw";

/**
 * Calculate approximate area of a closed polygon in square kilometers
 */
function calculatePolygonAreaSqKm(coords) {
  if (!coords || coords.length < 3) return 0;
  var area = 0;
  var numPoints = coords.length;
  for (var i = 0; i < numPoints; i++) {
    var j = (i + 1) % numPoints;
    var xi = coords[i][1] * (Math.PI / 180) * 6371 * Math.cos(coords[i][0] * (Math.PI / 180));
    var yi = coords[i][0] * (Math.PI / 180) * 6371;
    var xj = coords[j][1] * (Math.PI / 180) * 6371 * Math.cos(coords[j][0] * (Math.PI / 180));
    var yj = coords[j][0] * (Math.PI / 180) * 6371;
    area += xi * yj - xj * yi;
  }
  return Math.abs(area / 2).toFixed(2);
}

const AddEdit_AreaZoneMaster = () => {
  const API_URL_SAVE = "AreaZoneMaster/0/token";
  const API_URL_EDIT = API_WEB_URLS.MASTER + "/0/token/AreaZoneMasterEdit/Id";
  const API_URL_ALL_ZONES = API_WEB_URLS.MASTER + "/0/token/AreaZoneMaster/Id/0";
  const API_URL_CITY = API_WEB_URLS.MASTER + "/0/token/CityMaster/Id/0";
  const API_URL_VEHICLE_TYPE = API_WEB_URLS.MASTER + "/0/token/VehicleType/Id/0";

  const [state, setState] = useState({
    id: 0,
    formData: {
      ZoneName: "",
      ZoneType: "BLOCKED",
      F_CityMaster: "1", // Default Jodhpur
      F_VehicleType: "",
      BlockedReason: "",
      IsActive: true,
    },
  });

  const [points, setPoints] = useState([]); // Array of [lat, lng]
  const [isPolygonClosed, setIsPolygonClosed] = useState(false);
  const [isDrawingMode, setIsDrawingMode] = useState(true);
  const [cityList, setCityList] = useState([]);
  const [vehicleTypeList, setVehicleTypeList] = useState([]);
  const [otherZones, setOtherZones] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const currentPolygonRef = useRef(null);
  const activePolylineRef = useRef(null);
  const rubberbandPolylineRef = useRef(null);
  const tempMarkersRef = useRef([]);
  const startMarkerRef = useRef(null);
  const inProgressPointsRef = useRef([]);
  const otherZoneOverlaysRef = useRef([]);
  const searchInputRef = useRef(null);
  const isDrawingRef = useRef(true);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Keep ref in sync
  isDrawingRef.current = isDrawingMode;

  // 1. Load initial data from database
  useEffect(() => {
    Fn_FillListData(dispatch, setCityList, "gridData", API_URL_CITY);
    Fn_FillListData(dispatch, setVehicleTypeList, "gridData", API_URL_VEHICLE_TYPE);
    Fn_FillListData(dispatch, setOtherZones, "gridData", API_URL_ALL_ZONES);

    const editId = (location.state && location.state.Id) || 0;
    if (editId > 0) {
      Fn_FillListData(
        dispatch,
        function (dataList) {
          if (dataList && dataList.length > 0) {
            const rec = dataList[0];
            setState({
              id: rec.Id || 0,
              formData: {
                ZoneName: rec.ZoneName || "",
                ZoneType: rec.ZoneType || "BLOCKED",
                F_CityMaster: rec.F_CityMaster ? String(rec.F_CityMaster) : "1",
                F_VehicleType: rec.F_VehicleType ? String(rec.F_VehicleType) : "",
                BlockedReason: rec.BlockedReason || "",
                IsActive: rec.IsActive !== undefined && rec.IsActive !== null ? rec.IsActive : true,
              },
            });

            try {
              const raw = rec.CoordinatesJson || rec.PolygonCoords || "[]";
              const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
              if (Array.isArray(parsed) && parsed.length >= 3) {
                const loadedPoints = parsed.map(function (p) {
                  return [
                    p.lat !== undefined ? p.lat : p[0],
                    p.lng !== undefined ? p.lng : p[1],
                  ];
                });
                setPoints(loadedPoints);
                setIsPolygonClosed(true);
                setIsDrawingMode(false);
              }
            } catch (e) {
              setPoints([]);
            }
          }
        },
        "gridData",
        API_URL_EDIT + "/" + editId
      );
    }
  }, [dispatch, location.state]);

  // 2. Initialize Google Maps without the deprecated DrawingManager
  useEffect(() => {
    function initMap() {
      if (!mapContainerRef.current) return;
      if (!window.google || !window.google.maps) return;

      const initialCenter =
        points.length > 0
          ? { lat: points[0][0], lng: points[0][1] }
          : { lat: 26.2800, lng: 73.0200 };

      const map = new window.google.maps.Map(mapContainerRef.current, {
        center: initialCenter,
        zoom: points.length > 0 ? 14 : 12,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: window.google.maps.ControlPosition.TOP_RIGHT,
        },
        streetViewControl: false,
        fullscreenControl: true,
        draggableCursor: isDrawingRef.current ? "crosshair" : "grab",
        disableDoubleClickZoom: true, // We use double-click to close polygon!
      });

      mapInstanceRef.current = map;

      // In-flight active polyline
      const isAllowed = state.formData.ZoneType === "ALLOWED";
      activePolylineRef.current = new window.google.maps.Polyline({
        map: map,
        strokeColor: isAllowed ? "#16a34a" : "#dc2626",
        strokeWeight: 2.5,
        zIndex: 20,
      });

      // Rubberband line following mouse cursor
      rubberbandPolylineRef.current = new window.google.maps.Polyline({
        map: map,
        strokeColor: isAllowed ? "#16a34a" : "#dc2626",
        strokeWeight: 1.5,
        strokeOpacity: 0.7,
        zIndex: 19,
      });

      // Click on map to add points to in-progress polygon
      map.addListener("click", function (e) {
        if (!isDrawingRef.current) return;
        handleAddDrawPoint(e.latLng.lat(), e.latLng.lng());
      });

      // Move mouse to update rubberband guide line
      map.addListener("mousemove", function (e) {
        if (!isDrawingRef.current) return;
        const pts = inProgressPointsRef.current;
        if (pts.length > 0 && rubberbandPolylineRef.current) {
          const last = pts[pts.length - 1];
          rubberbandPolylineRef.current.setPath([
            { lat: last[0], lng: last[1] },
            e.latLng,
          ]);
        }
      });

      // Double-click to close polygon
      map.addListener("dblclick", function (e) {
        if (!isDrawingRef.current) return;
        if (e && e.latLng) {
          handleAddDrawPoint(e.latLng.lat(), e.latLng.lng());
        }
        handleClosePolygon();
      });

      // Google Places Autocomplete
      if (searchInputRef.current && window.google.maps.places) {
        const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
          componentRestrictions: { country: "in" },
          fields: ["geometry", "name", "formatted_address"],
        });
        autocomplete.bindTo("bounds", map);

        autocomplete.addListener("place_changed", function () {
          const place = autocomplete.getPlace();
          if (!place || !place.geometry || !place.geometry.location) return;

          if (place.geometry.viewport) {
            map.fitBounds(place.geometry.viewport);
          } else {
            map.setCenter(place.geometry.location);
            map.setZoom(16);
          }
        });
      }

      setMapLoaded(true);
    }

    if (window.google && window.google.maps && window.google.maps.places) {
      initMap();
    } else {
      const scriptId = "google-maps-api-script";
      let script = document.getElementById(scriptId);
      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        // NOTICE: NO drawing library! Only places,geometry to avoid v3.65 deprecation error
        script.src =
          "https://maps.googleapis.com/maps/api/js?key=" +
          GOOGLE_MAPS_KEY +
          "&libraries=places,geometry";
        script.async = true;
        script.defer = true;
        script.onload = initMap;
        document.head.appendChild(script);
      } else {
        script.addEventListener("load", initMap);
      }
    }
  }, []);

  // 3. Add point while drawing custom polygon
  function handleAddDrawPoint(lat, lng) {
    const newPt = [Number(lat.toFixed(6)), Number(lng.toFixed(6))];
    const updated = [...inProgressPointsRef.current, newPt];
    inProgressPointsRef.current = updated;
    setPoints(updated);

    const isAllowed = state.formData.ZoneType === "ALLOWED";
    const stroke = isAllowed ? "#16a34a" : "#dc2626";

    // Update active polyline path
    if (activePolylineRef.current) {
      const gPath = updated.map(function (p) {
        return { lat: p[0], lng: p[1] };
      });
      activePolylineRef.current.setPath(gPath);
    }

    // If first point, add prominent Start Point Marker that closes polygon when clicked!
    if (updated.length === 1 && mapInstanceRef.current && window.google) {
      if (startMarkerRef.current) startMarkerRef.current.setMap(null);

      const startMarker = new window.google.maps.Marker({
        position: { lat: newPt[0], lng: newPt[1] },
        map: mapInstanceRef.current,
        title: "Click here to close the polygon!",
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: stroke,
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2.5,
        },
        zIndex: 30,
        cursor: "pointer",
      });

      startMarker.addListener("click", function () {
        handleClosePolygon();
      });

      startMarkerRef.current = startMarker;
    } else if (mapInstanceRef.current && window.google) {
      // Small dot for subsequent vertices while drawing
      const dot = new window.google.maps.Marker({
        position: { lat: newPt[0], lng: newPt[1] },
        map: mapInstanceRef.current,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 4.5,
          fillColor: stroke,
          fillOpacity: 0.9,
          strokeColor: "#ffffff",
          strokeWeight: 1.5,
        },
        zIndex: 25,
      });
      tempMarkersRef.current.push(dot);
    }
  }

  // 4. Close and finalize polygon
  function handleClosePolygon() {
    const pts = inProgressPointsRef.current;
    if (!pts || pts.length < 3) {
      alert("A closed polygon requires at least 3 points. Please click on the map to place more corners.");
      return;
    }

    renderPolygonFromPoints(pts, state.formData.ZoneType === "ALLOWED");
    clearDrawingArtifacts();
    setIsDrawingMode(false);
    setIsPolygonClosed(true);

    if (mapInstanceRef.current) {
      mapInstanceRef.current.setOptions({ draggableCursor: "grab" });
    }
  }

  // Clean up in-flight drawing markers and temporary lines
  function clearDrawingArtifacts() {
    if (activePolylineRef.current) {
      activePolylineRef.current.setPath([]);
    }
    if (rubberbandPolylineRef.current) {
      rubberbandPolylineRef.current.setPath([]);
    }
    if (startMarkerRef.current) {
      startMarkerRef.current.setMap(null);
      startMarkerRef.current = null;
    }
    tempMarkersRef.current.forEach(function (m) {
      m.setMap(null);
    });
    tempMarkersRef.current = [];
  }

  // 5. Setup native editable polygon with Google's drag handles and ghost midpoints
  function setupEditablePolygon(polygon) {
    if (currentPolygonRef.current && currentPolygonRef.current !== polygon) {
      currentPolygonRef.current.setMap(null);
    }
    currentPolygonRef.current = polygon;

    const path = polygon.getPath();

    function syncPathToState() {
      const len = path.getLength();
      const coords = [];
      for (let i = 0; i < len; i++) {
        const latLng = path.getAt(i);
        coords.push([
          Number(latLng.lat().toFixed(6)),
          Number(latLng.lng().toFixed(6)),
        ]);
      }
      setPoints(coords);
      inProgressPointsRef.current = coords;
      setIsPolygonClosed(len >= 3);
    }

    path.addListener("set_at", syncPathToState);
    path.addListener("insert_at", syncPathToState);
    path.addListener("remove_at", syncPathToState);

    // Right-click on any vertex handle to delete it
    window.google.maps.event.addListener(polygon, "rightclick", function (mev) {
      if (mev.vertex !== undefined) {
        if (path.getLength() <= 3) {
          alert("A closed polygon must have at least 3 points.");
          return;
        }
        path.removeAt(mev.vertex);
      }
    });

    syncPathToState();
  }

  // 6. Render polygon from points
  function renderPolygonFromPoints(pts, isAllowedType) {
    if (!mapInstanceRef.current || !window.google) return;
    const isAllowed = isAllowedType !== undefined ? isAllowedType : state.formData.ZoneType === "ALLOWED";

    if (currentPolygonRef.current) {
      currentPolygonRef.current.setMap(null);
      currentPolygonRef.current = null;
    }

    if (!pts || pts.length < 3) {
      setPoints(pts || []);
      setIsPolygonClosed(false);
      return;
    }

    const gCoords = pts.map(function (p) {
      return { lat: p[0], lng: p[1] };
    });

    // Native Google Maps Polygon with editable: true
    const polygon = new window.google.maps.Polygon({
      paths: gCoords,
      editable: true,
      draggable: false,
      fillColor: isAllowed ? "#22c55e" : "#ef4444",
      fillOpacity: 0.35,
      strokeColor: isAllowed ? "#16a34a" : "#dc2626",
      strokeWeight: 2.5,
      zIndex: 10,
    });

    polygon.setMap(mapInstanceRef.current);
    setupEditablePolygon(polygon);

    // Fit map bounds to show complete polygon
    const bounds = new window.google.maps.LatLngBounds();
    gCoords.forEach(function (c) {
      bounds.extend(c);
    });
    mapInstanceRef.current.fitBounds(bounds);
  }

  // When points load from DB on Edit, render on Google Map
  useEffect(() => {
    if (mapLoaded && points.length >= 3 && !currentPolygonRef.current) {
      renderPolygonFromPoints(points, state.formData.ZoneType === "ALLOWED");
    }
  }, [mapLoaded, points]);

  // Render other existing saved zones as reference outlines
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !window.google) return;

    otherZoneOverlaysRef.current.forEach(function (o) {
      o.setMap(null);
    });
    otherZoneOverlaysRef.current = [];

    otherZones.forEach(function (z) {
      if (state.id > 0 && String(z.Id) === String(state.id)) {
        return;
      }
      try {
        const raw = z.CoordinatesJson || z.PolygonCoords || "[]";
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (!Array.isArray(parsed) || parsed.length < 3) return;

        const isZAllowed = z.ZoneType === "ALLOWED";
        const gCoords = parsed.map(function (p) {
          return {
            lat: p.lat !== undefined ? p.lat : p[0],
            lng: p.lng !== undefined ? p.lng : p[1],
          };
        });

        const refPolygon = new window.google.maps.Polygon({
          paths: gCoords,
          editable: false,
          clickable: true,
          fillColor: isZAllowed ? "#22c55e" : "#ef4444",
          fillOpacity: 0.12,
          strokeColor: isZAllowed ? "#16a34a" : "#dc2626",
          strokeWeight: 1.5,
          zIndex: 1,
        });

        refPolygon.setMap(mapInstanceRef.current);

        const infoWindow = new window.google.maps.InfoWindow({
          content: `<div style="font-size:12px;font-family:sans-serif;">
            <strong>${z.ZoneName}</strong><br/>
            <span style="color:${isZAllowed ? '#16a34a' : '#dc2626'};font-weight:bold;">
              ${isZAllowed ? 'Allowed Serviceable Area' : 'Restricted No-Entry Area'}
            </span>
          </div>`,
        });

        window.google.maps.event.addListener(refPolygon, "click", function (e) {
          infoWindow.setPosition(e.latLng);
          infoWindow.open(mapInstanceRef.current);
        });

        otherZoneOverlaysRef.current.push(refPolygon);
      } catch (err) {
        // Skip invalid coordinates
      }
    });
  }, [mapLoaded, otherZones, state.id]);

  // Update polygon colors if ZoneType changes between ALLOWED / BLOCKED
  const handleZoneTypeChange = (newType, setFieldValue) => {
    setFieldValue("ZoneType", newType);
    const isAllowed = newType === "ALLOWED";
    const stroke = isAllowed ? "#16a34a" : "#dc2626";
    const fill = isAllowed ? "#22c55e" : "#ef4444";

    if (currentPolygonRef.current) {
      currentPolygonRef.current.setOptions({
        fillColor: fill,
        strokeColor: stroke,
      });
    }
    if (activePolylineRef.current) {
      activePolylineRef.current.setOptions({ strokeColor: stroke });
    }
    if (rubberbandPolylineRef.current) {
      rubberbandPolylineRef.current.setOptions({ strokeColor: stroke });
    }
  };

  // Start / Reset Custom Drawing
  const handleStartDrawCustomShape = () => {
    if (currentPolygonRef.current) {
      currentPolygonRef.current.setMap(null);
      currentPolygonRef.current = null;
    }
    clearDrawingArtifacts();
    inProgressPointsRef.current = [];
    setPoints([]);
    setIsPolygonClosed(false);
    setIsDrawingMode(true);

    if (mapInstanceRef.current) {
      mapInstanceRef.current.setOptions({ draggableCursor: "crosshair" });
    }
  };

  // Fit map to current polygon
  const handleFitPolygon = () => {
    if (!mapInstanceRef.current || !currentPolygonRef.current || points.length < 2) return;
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach(function (p) {
      bounds.extend({ lat: p[0], lng: p[1] });
    });
    mapInstanceRef.current.fitBounds(bounds);
  };

  const validationSchema = Yup.object().shape({
    ZoneName: Yup.string().required("Zone Name is required"),
    ZoneType: Yup.string().required("Zone Type is required"),
  });

  const handleSubmit = (values, formikHelpers) => {
    if (points.length < 3 || !isPolygonClosed) {
      alert("Please complete and close your custom polygon on the map before saving.");
      formikHelpers.setSubmitting(false);
      return;
    }

    const coordinates = points.map(function (p) {
      return { lat: p[0], lng: p[1] };
    });
    const isAllowed = values.ZoneType === "ALLOWED";

    const formData = new FormData();
    formData.append("Id", state.id || 0);
    formData.append("ZoneName", values.ZoneName);
    formData.append("ZoneType", values.ZoneType);
    formData.append("F_CityMaster", values.F_CityMaster || "");
    formData.append("F_VehicleType", values.F_VehicleType || "");
    formData.append("CoordinatesJson", JSON.stringify(coordinates));
    formData.append("BlockedReason", values.BlockedReason || "");
    formData.append("StrokeColor", isAllowed ? "#22c55e" : "#ef4444");
    formData.append("FillColor", isAllowed ? "#22c55e33" : "#ef444433");
    formData.append("IsActive", values.IsActive ? "true" : "false");

    let currentUserId = 0;
    try {
      const rawUser = localStorage.getItem("user");
      const rawAuth = localStorage.getItem("authUser");
      let userObj = null;
      if (rawUser && rawUser !== "undefined" && rawUser !== "null") {
        userObj = JSON.parse(rawUser);
      } else if (rawAuth && rawAuth !== "undefined" && rawAuth !== "null") {
        userObj = JSON.parse(rawAuth);
      }
      if (userObj) {
        currentUserId = userObj.uid || userObj.Id || userObj.id || userObj.userId || 0;
      }
    } catch (parseErr) {
      console.warn("Could not parse user from localStorage:", parseErr);
    }
    formData.append("UserId", currentUserId);

    Fn_AddEditData(
      dispatch,
      setState,
      { arguList: { id: state.id, formData: formData } },
      API_URL_SAVE,
      true,
      "zoneid",
      navigate,
      "/AreaZoneMaster"
    ).finally(function () {
      if (formikHelpers && formikHelpers.setSubmitting) {
        formikHelpers.setSubmitting(false);
      }
    });
  };

  const approxArea = calculatePolygonAreaSqKm(points);

  return (
    <div style={{ padding: "8px 12px", maxWidth: 1440, margin: "0 auto" }}>
      {/* Top Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
          paddingBottom: 8,
          borderBottom: "1px solid #dee2e6",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Button
            size="sm"
            variant="outline-secondary"
            className="py-1 px-2 d-flex align-items-center gap-1"
            onClick={function () {
              navigate("/AreaZoneMaster");
            }}
            style={{ fontSize: 12 }}
          >
            <ArrowLeft size={14} /> Back
          </Button>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>
            {state.id > 0 ? "Edit Closed Polygon Zone" : "Draw Custom Closed Polygon Zone"}
          </span>
          <Badge
            color={isPolygonClosed && points.length >= 3 ? "success" : "warning"}
            style={{ fontSize: 11, padding: "4px 8px" }}
          >
            {isPolygonClosed && points.length >= 3
              ? `✓ Closed Polygon (${points.length} Points, ~${approxArea} km²)`
              : `Drawing In-Progress (${points.length} points placed)`}
          </Badge>
        </div>
      </div>

      <Formik
        enableReinitialize
        initialValues={state.formData}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {function (formikProps) {
          const values = formikProps.values;
          const errors = formikProps.errors;
          const touched = formikProps.touched;
          const isSubmitting = formikProps.isSubmitting;
          const setFieldValue = formikProps.setFieldValue;

          const currentIsAllowed = values.ZoneType === "ALLOWED";

          return (
            <Form>
              <Row>
                {/* Left Side: Zone Details */}
                <Col lg={4}>
                  <Card style={{ border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", marginBottom: 12 }}>
                    <CardBody style={{ padding: 14 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 10 }}>
                        Zone Details
                      </div>

                      {/* Zone Name */}
                      <div className="mb-2">
                        <label style={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>
                          Zone Name <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="text"
                          name="ZoneName"
                          placeholder="e.g. Jodhpur City Serviceable Area or Old City No-Entry"
                          className={
                            "form-control form-control-sm " +
                            (touched.ZoneName && errors.ZoneName ? "is-invalid" : "")
                          }
                          style={{ fontSize: 12 }}
                        />
                        <ErrorMessage name="ZoneName" component="div" className="invalid-feedback" style={{ fontSize: 10 }} />
                      </div>

                      {/* Zone Purpose / Type */}
                      <div className="mb-2">
                        <label style={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>
                          Zone Purpose / Type <span className="text-danger">*</span>
                        </label>
                        <div style={{ display: "flex", gap: 8 }}>
                          <Button
                            type="button"
                            size="sm"
                            style={{
                              flex: 1,
                              fontSize: 11,
                              backgroundColor: currentIsAllowed ? "#16a34a" : "#f1f5f9",
                              color: currentIsAllowed ? "#fff" : "#475569",
                              borderColor: currentIsAllowed ? "#15803d" : "#cbd5e1",
                              fontWeight: currentIsAllowed ? 700 : 500,
                            }}
                            onClick={function () {
                              handleZoneTypeChange("ALLOWED", setFieldValue);
                            }}
                          >
                            🟢 ALLOWED (Serviceable)
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            style={{
                              flex: 1,
                              fontSize: 11,
                              backgroundColor: !currentIsAllowed ? "#dc2626" : "#f1f5f9",
                              color: !currentIsAllowed ? "#fff" : "#475569",
                              borderColor: !currentIsAllowed ? "#b91c1c" : "#cbd5e1",
                              fontWeight: !currentIsAllowed ? 700 : 500,
                            }}
                            onClick={function () {
                              handleZoneTypeChange("BLOCKED", setFieldValue);
                            }}
                          >
                            🔴 BLOCKED (No-Entry)
                          </Button>
                        </div>
                        <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>
                          {currentIsAllowed
                            ? "✅ Customer rides allowed inside this closed area (outside will be blocked)."
                            : "🚫 Customer rides prohibited inside this closed area (e.g. narrow lanes, no trucks)."}
                        </div>
                      </div>

                      {/* City Selection */}
                      <div className="mb-2">
                        <label style={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>City</label>
                        <Field as="select" name="F_CityMaster" className="form-select form-select-sm" style={{ fontSize: 12 }}>
                          <option value="">-- Select City --</option>
                          {cityList.map(function (c) {
                            return (
                              <option key={c.Id} value={c.Id}>
                                {c.Name}
                              </option>
                            );
                          })}
                        </Field>
                      </div>

                      {/* Vehicle Restriction */}
                      <div className="mb-2">
                        <label style={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>
                          Vehicle Type Restriction (Optional)
                        </label>
                        <Field as="select" name="F_VehicleType" className="form-select form-select-sm" style={{ fontSize: 12 }}>
                          <option value="">-- All Vehicles (Applies to everyone) --</option>
                          {vehicleTypeList.map(function (v) {
                            return (
                              <option key={v.Id} value={v.Id}>
                                {v.Name}
                              </option>
                            );
                          })}
                        </Field>
                      </div>

                      {/* Blocked Reason */}
                      {!currentIsAllowed && (
                        <div className="mb-2">
                          <label style={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>
                            Customer Explanation / Alert
                          </label>
                          <Field
                            as="textarea"
                            rows="2"
                            name="BlockedReason"
                            placeholder="e.g. Commercial vehicles cannot enter Old City / narrow market roads."
                            className="form-control form-control-sm"
                            style={{ fontSize: 11 }}
                          />
                        </div>
                      )}

                      {/* Active Status */}
                      <div className="form-check form-switch mb-3">
                        <Field
                          type="checkbox"
                          id="ActiveStatusSwitch"
                          name="IsActive"
                          className="form-check-input"
                          checked={values.IsActive}
                          onChange={function (e) {
                            setFieldValue("IsActive", e.target.checked);
                          }}
                        />
                        <label htmlFor="ActiveStatusSwitch" className="form-check-label" style={{ fontSize: 11, fontWeight: 600 }}>
                          Zone Active in App
                        </label>
                      </div>

                      <hr style={{ borderColor: "#e2e8f0", margin: "10px 0" }} />

                      {/* Shape Status Card */}
                      <div
                        style={{
                          padding: "10px",
                          borderRadius: 6,
                          backgroundColor: isPolygonClosed && points.length >= 3 ? "#f0fdf4" : "#fef2f2",
                          border: `1px solid ${isPolygonClosed && points.length >= 3 ? "#bbf7d0" : "#fecaca"}`,
                          marginBottom: 12,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: isPolygonClosed && points.length >= 3 ? "#15803d" : "#b91c1c",
                            marginBottom: 4,
                          }}
                        >
                          {isPolygonClosed && points.length >= 3
                            ? "✓ Valid Closed Polygon"
                            : "⚠️ Polygon Not Closed Yet"}
                        </div>
                        <div style={{ fontSize: 11, color: "#334155" }}>
                          Corner Points: <strong>{points.length}</strong>
                        </div>
                        {isPolygonClosed && points.length >= 3 && (
                          <div style={{ fontSize: 11, color: "#334155" }}>
                            Enclosed Area: <strong>~{approxArea} km²</strong>
                          </div>
                        )}
                        <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>
                          {isPolygonClosed && points.length >= 3
                            ? "💡 Drag corner handles to fine-tune. Drag middle ghost handles to add more corners. Right-click handle to delete."
                            : "Click corners on the map, then click the starting point (or 'Close Shape' button) to complete."}
                        </div>
                      </div>

                      {/* Action & Save */}
                      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline-danger"
                          className="py-1 px-2 d-flex align-items-center justify-content-center gap-1"
                          style={{ fontSize: 11, flex: 1 }}
                          onClick={handleStartDrawCustomShape}
                        >
                          <RotateCcw size={12} /> Clear & Redraw Shape
                        </Button>
                      </div>

                      <Button
                        type="submit"
                        color="primary"
                        disabled={isSubmitting || !isPolygonClosed || points.length < 3}
                        style={{ width: "100%", fontSize: 13, padding: "8px 16px" }}
                        className="d-flex align-items-center justify-content-center gap-2"
                      >
                        <Save size={15} />
                        {isSubmitting ? "Saving..." : "Save Closed Geofence Zone"}
                      </Button>
                    </CardBody>
                  </Card>
                </Col>

                {/* Right Side: Google Maps Interactive Canvas */}
                <Col lg={8}>
                  <Card style={{ border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                    <CardBody style={{ padding: 10 }}>
                      {/* Top Action Bar */}
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 8,
                          padding: "6px 10px",
                          backgroundColor: "#f8fafc",
                          borderRadius: 6,
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        {/* Drawing Actions */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {isDrawingMode && points.length >= 3 && (
                            <Button
                              type="button"
                              size="sm"
                              color="success"
                              style={{ fontSize: 11, padding: "4px 12px", fontWeight: 700 }}
                              className="d-flex align-items-center gap-1"
                              onClick={handleClosePolygon}
                            >
                              <Check size={13} /> ✓ Close & Complete Shape ({points.length} Points)
                            </Button>
                          )}

                          <Button
                            type="button"
                            size="sm"
                            color="primary"
                            style={{ fontSize: 11, padding: "4px 10px" }}
                            className="d-flex align-items-center gap-1"
                            onClick={handleStartDrawCustomShape}
                          >
                            <Edit2 size={12} /> ⬡ Draw New Shape
                          </Button>

                          {isPolygonClosed && points.length >= 3 && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline-secondary"
                              style={{ fontSize: 11, padding: "4px 8px" }}
                              title="Fit map to your polygon"
                              onClick={handleFitPolygon}
                            >
                              <Maximize2 size={12} /> Fit Shape to View
                            </Button>
                          )}
                        </div>

                        {/* Status Label */}
                        <div style={{ fontSize: 11, fontWeight: 600, color: isPolygonClosed ? "#16a34a" : "#ea580c" }}>
                          {isPolygonClosed
                            ? `✓ Closed Polygon (${points.length} Points)`
                            : `Drawing: ${points.length} Points Placed (Click start dot or double-click to close)`}
                        </div>
                      </div>

                      {/* Google Places Search Box */}
                      <div style={{ position: "relative", marginBottom: 8 }}>
                        <input
                          ref={searchInputRef}
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="🔍 Search any city, colony, road or landmark (e.g. Ghanta Ghar, Sardarpura, AIIMS Jodhpur)..."
                          style={{ fontSize: 12, paddingLeft: 12 }}
                        />
                      </div>

                      {/* Simple Step-by-Step Guidance Banner */}
                      <div
                        style={{
                          padding: "8px 12px",
                          borderRadius: 4,
                          marginBottom: 8,
                          fontSize: 11,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          backgroundColor: isPolygonClosed ? "#f0fdf4" : "#eff6ff",
                          border: `1px solid ${isPolygonClosed ? "#bbf7d0" : "#bfdbfe"}`,
                          color: isPolygonClosed ? "#15803d" : "#1e40af",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {isPolygonClosed ? (
                            <>
                              <CheckCircle size={15} />
                              <span>
                                <strong>Closed Polygon Ready!</strong> Drag <strong>corner handles</strong> to reposition. Drag <strong>middle ghost handles</strong> to pull out new corners. Right-click any handle to delete it.
                              </span>
                            </>
                          ) : (
                            <>
                              <Edit2 size={15} />
                              <span>
                                <strong>How to draw any custom shape:</strong> Click anywhere on the map to add corner 1, 2, 3... Click the <strong>first point</strong> (or double-click, or click the green <em>'Close & Complete Shape'</em> button) to close the loop!
                              </span>
                            </>
                          )}
                        </div>
                        <span style={{ fontSize: 10, opacity: 0.85 }}>
                          Right-click handle to delete.
                        </span>
                      </div>

                      {/* Google Map Container Element */}
                      <div
                        ref={mapContainerRef}
                        style={{
                          height: "590px",
                          width: "100%",
                          borderRadius: 6,
                          overflow: "hidden",
                          border: "1px solid #cbd5e1",
                        }}
                      />

                      {/* Vertices coordinates list */}
                      {points.length > 0 && (
                        <div
                          style={{
                            marginTop: 8,
                            padding: "6px 10px",
                            backgroundColor: "#f8fafc",
                            borderRadius: 4,
                            border: "1px solid #e2e8f0",
                            maxHeight: 65,
                            overflowY: "auto",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 4,
                            alignItems: "center",
                          }}
                        >
                          <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", marginRight: 4 }}>
                            Polygon Points ({points.length}):
                          </span>
                          {points.map(function (pt, idx) {
                            return (
                              <span
                                key={idx}
                                style={{
                                  fontSize: 10,
                                  backgroundColor: "#ffffff",
                                  border: "1px solid #cbd5e1",
                                  borderRadius: 12,
                                  padding: "1px 6px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  color: "#334155",
                                }}
                              >
                                <strong>#{idx + 1}</strong>: {pt[0].toFixed(4)}, {pt[1].toFixed(4)}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default AddEdit_AreaZoneMaster;
